import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ZakupyChangesByCountry = ({ purchaseData = [] }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [hiddenCountries, setHiddenCountries] = useState(new Set());

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  useEffect(() => {
    if (purchaseData.length === 0) return;
    const years = [...new Set(purchaseData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[0]);
  }, [purchaseData]);

  useEffect(() => {
    if (!selectedYear || purchaseData.length === 0) return;

    const monthlyData = Array.from({ length: 12 }, (_, i) => ({ month: monthNames[i] }));

    purchaseData
      .filter((row) => row.Rok.toString() === selectedYear)
      .forEach((row) => {
        const monthIndex = parseInt(row.Miesiac, 10) - 1;
        const country = row.KodKrajuNadaniaTIN?.trim() || "Unknown";
        const value = (parseFloat(row.K_42) || 0) + (parseFloat(row.K_43) || 0);

        if (!monthlyData[monthIndex][country]) {
          monthlyData[monthIndex][country] = 0;
        }
        monthlyData[monthIndex][country] += value;
      });

    setFilteredData(monthlyData);
  }, [purchaseData, selectedYear]);

  const toggleCountry = (country) => {
    setHiddenCountries((prev) => {
      const next = new Set(prev);
      next.has(country) ? next.delete(country) : next.add(country);
      return next;
    });
  };

  return (
    <Wrapper>
      <ChartTitle>
        Zmiany zakupów międzynarodowych w czasie
        <InfoIconWrapper
          data-tooltip-id="zakupy-tooltip"
          data-tooltip-content="Zmiany zakupów miesięcznie wg kraju. Kliknij legendę, by ukryć/odsłonić."
        >
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip id="zakupy-tooltip" place="left" />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      <ResponsiveContainer width="100%" height={550}>
        <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis tickFormatter={(value) => value.toLocaleString("pl-PL")} />
          <Tooltip formatter={(value) => `${value.toLocaleString("pl-PL")} PLN`} />
          <Legend onClick={(e) => toggleCountry(e.value)} wrapperStyle={{ cursor: "pointer" }} />
          {filteredData.length > 0 &&
            Object.keys(filteredData[0]).filter((key) => key !== "month").map((country, index) => (
              <Line
                key={country}
                type="monotone"
                dataKey={country}
                stroke={`hsl(${index * 25}, 60%, 45%)`}
                strokeWidth={2}
                dot={{ r: 3 }}
                hide={hiddenCountries.has(country)}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default ZakupyChangesByCountry;
